import React from "react";
import {Glob as G} from "../libs/Glob.mjs";
import {Button, ButtonGroup } from "reactstrap";
import ExitIcon from "../media/images/ExitIcon.mjs";
import TextLabel from "./TextLabel.mjs";
import ReloadIcon from "../media/images/ReloadIcon.mjs";
import CdIcon from "../media/images/CdIcon.mjs";
import CheckIcon from "../media/images/CheckIcon.mjs";
import UploadIcon from "../media/images/UploadIcon.mjs";
import DownloadIcon from "../media/images/DownloadIcon.mjs";

/**
 * @typedef ButtonsExitReloadSaveProps
 * @type {object}
 * @property {number} [changes] Defaults to 0
 * @property {string} changesKey Trigger key to register for updates, This is the only mandatory attribute, registration must return a number.
 * @property {string|JSX.Element|boolean} [valid] Whether to display validity.
 * @property {string} [validKey] Trigger key to register for updates on "valid" string, if any.
 * @property {function|boolean} [exit] Triggered to exit (if function), works as "back" if true
 * @property {string|JSX.Element|false} [exitLabel]
 * @property {string} [exitTitle]
 * @property {function} [save] Triggered to save
 * @property {string|JSX.Element|false} [saveLabel]
 * @property {string} [saveTitle]
 * @property {function} [reload] Triggered to reload
 * @property {string|JSX.Element|false} [reloadLabel]
 * @property {string} [reloadTitle]
 * @property {function} [download]
 * @property {string|JSX.Element|false} [downloadLabel]
 * @property {string} [downloadTitle]
 * @property {function} [upload]
 * @property {string|JSX.Element|false} [uploadLabel]
 * @property {string} [uploadTitle]
 */

/**
 * @typedef ButtonsExitReloadSaveState
 * @type {object}
 * @property {number} changes
 * @property {boolean} busy
 * @property {boolean} [hasValid]
 * @property {string|JSX.Element} [valid]
 */

/**
 * @private
 * @param {boolean|string|JSX.Element} elem
 */
const buildValid = (elem) => {
	let ret;
	switch ( typeof elem ) {
		case "undefined": return <span/>;
		case "string": ret = elem; break;
		case "number": ret = String(elem); break;
		case "boolean": ret = elem ? <CheckIcon key={true} /> : <TextLabel key={false} value={"delete"} lang={"icon"} />; break;
		default: break;
	}
	if ( typeof ret === "string" ) return <Button color={"light"} className={"btn-icon"} disabled={true}><b>{ ret }</b></Button>;
	if ( typeof ret !== "undefined" ) return <Button color={"light"} className={"btn-icon"} disabled={true}><i>{ ret }</i></Button>;
	return elem;
}

/**
 * Detached buttonbar for Exit / Revert? / Save
 * @desc In parameters specify event keys to be triggered for the different actions.
 * @summary The component represents a detached control, it can be placed in a different location/hierarchy (optionally by Glob.trigger()'ing it).
 * @summary This component will register itself for changesKey, the is supposed to deliver a number.
 * @summary In case a function isn't passed the related button won't show.
 * @param {ButtonsExitReloadSaveProps} props
 * @param {number} [props.changes] Defaults to 0
 * @param {string} props.changesKey Trigger key to register for updates. This is the only mandatory attribute, registration must return a number.
 * @param {string|JSX.Element|boolean} [props.valid] Whether to display validity.
 * @param {string} [props.validKey] Trigger key to register for updates on "valid" string, if any.
 * @param {function|boolean} [props.exit] Triggered to exit (if function), works as "back" if true
 * @param {string|JSX.Element|false} [props.exitLabel]
 * @param {string} [props.exitTitle]
 * @param {function} [props.save] Triggered to save
 * @param {string|JSX.Element|false} [props.saveLabel]
 * @param {string} [props.saveTitle]
 * @param {function} [props.reload] Triggered to reload
 * @param {string|JSX.Element|false} [props.saveLabel]
 * @param {function} [props.download]
 * @param {string|JSX.Element|false} [props.downloadLabel]
 * @param {string} [props.downloadTitle]
 * @param {function} [props.upload]
 * @param {string|JSX.Element|false} [props.uploadLabel]
 * @param {string} [props.uploadTitle]
 * @see Glob.trigger
 * @return {JSX.Element}
 */
const ButtonsExitReloadSave = class extends React.Component {
	/**
	 * @constructor
	 * @param {ButtonsExitReloadSaveProps} props
	 */
	constructor(props) {
		super(props);
		/** @type { ButtonsExitReloadSaveState } */
		this.state = {
			changes: props?.changes || G.getStatus(props.changesKey) || 0,
			busy: false,
			hasValid: false,
			validKey: 'ButtonsExitReloadSaveNoValidKey'
		};
		if ( typeof props.validKey === "string" ) {
			this.state.validKey = props.validKey;
			this.state.hasValid = true;
			this.state.validFlag = !!props.valid;
			this.state.validSign = buildValid(typeof props.valid !== "undefined" ? props.valid : false);
		}
		this.uid = G.generateUid('ButtonsExitReloadSave');
	}
	componentDidMount() {
		G.register( this.props.changesKey, qt => {
			this.setState({ changes: qt, busy: false });
		}, this.uid);
		if ( this.state.hasValid ) {
			G.register( this.state.validKey, v => {
				this.setState({ validFlag: v, validSign: buildValid(v) });
			}, this.uid);
		}
	}
	componentWillUnmount() {
		G.deregister(this.uid);
	}
	render() {
		return(
			<div
				className={"ButtonsExitReloadSave"}
				id={this.uid}
			>
				{ this.state.hasValid && this.state.validSign }
				<ButtonGroup
					hidden={ typeof this.props.exit !== 'function' && ! this.props.exit }
					className={"btn-icon me-2"}
				>
					<Button
						disabled={ this.state.busy ||  this.state.changes !== 0}
						color={"warning"}
						title={ this.props.exitTitle ?? G.label('Exit') }
						onClick={ ()=>{
							if ( typeof this.props.exit === 'function') {
								this.props.exit();
								return;
							}
							const pg = G.getStatus('previousPage') || {
								name: 'Admin',
								params: {group: G.getPageParam('group'), tab: 'Groups'}
							};
							G.gotoPage( pg );
						}}
					>
						<i>
							<ExitIcon />
						</i>
						{ this.props.exitLabel === false ? '' : (
							<b>
								{this.props.exitLabel ?? <TextLabel value={"Exit"} />}
							</b>
						)}
					</Button>
				</ButtonGroup>
				{(typeof this.props.download === 'function' || typeof this.props.upload === 'function') && (
					<ButtonGroup
						className={"btn-icon me-2"}
						hidden={ typeof this.props.download !== 'function' && typeof this.props.upload !== 'function' }
					>
						{ typeof this.props.upload === 'function' && (
							<Button
								color={"secondary"}
								onClick={ this.props.upload }
								title={ this.props.uploadTitle ?? G.label('Upload') }
							>
								<i>
									<UploadIcon />
								</i>
								{ this.props.uploadLabel !== false && (
									<b>
										{this.props.uploadLabel ?? <TextLabel value={"Upload"} />}
									</b>
								)}
							</Button>
						)}
						{ typeof this.props.download === 'function' && (
							<Button
								color={"primary"}
								onClick={ this.props.download }
								title={ this.props.downloadTitle ?? G.label('Download') }
							>
								{ (this.props.downloadLabel !== false && typeof this.props.upload === 'function') && (
									<b>
										{this.props.downloadLabel ?? <TextLabel value={"Download"} />}
									</b>
								)}
								<i>
									<DownloadIcon />
								</i>
								{ (this.props.downloadLabel !== false && typeof this.props.upload !== 'function') && (
									<b>
										{this.props.downloadLabel ?? <TextLabel value={"Download"} />}
									</b>
								)}
							</Button>
						)}
					</ButtonGroup>
				)}
				{ (typeof this.props.reload === 'function' || typeof this.props.save === 'function') && (
					<ButtonGroup
						className={"btn-icon"}
					>
						{ typeof this.props.reload === 'function' && (
							<Button
								disabled={ this.state.busy || this.state.changes === 0 }
								color={"warning"}
								onClick={ this.props.reload }
								title={ this.props.reloadTitle ?? G.label('Revert') }
							>
								<i>
									<ReloadIcon/>
								</i>
								{ this.props.reloadLabel === false ? '' : (
									<b>
										{this.props.reloadLabel ?? <TextLabel value={"Revert"} /> }
									</b>
								)}
							</Button>
						)}
						{ typeof this.props.save === 'function' && (
							<Button
								disabled={ this.state.busy || this.state.changes === 0 }
								color={"success"}
								onClick={ this.props.save }
								title={ this.props.saveTitle ?? G.label('Save') }
							>
								{ this.props.saveTitle === false ? '' : (
									<b>
										{this.props.saveTitle ?? <TextLabel value={"Save"} /> }
									</b>
								)}
								<i>
									<CdIcon/>
								</i>
							</Button>
						)}
					</ButtonGroup>
				)}
			</div>
		)
	}
}

export default ButtonsExitReloadSave;
